import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRoutePlanLook = 'route-plan-toggle' | 'route-plan-back' | 'route-plan-far' | 'route-plan-unroutable';

const ROUTE_PLAN_LOOKS = new Set<string>(['route-plan-toggle', 'route-plan-back', 'route-plan-far', 'route-plan-unroutable']);
export const isWedgeRoutePlanLook = (look: string): look is WedgeRoutePlanLook => ROUTE_PLAN_LOOKS.has(look);

/* Released route plan list (Drive, "Plan my day"): four raw buttons around the stop list. The directions toggle (phone only) is a full width
   flex line with a 14px top margin, 11px by 14px of padding, a 12px radius, a 1px card border on the elevated fill, 14px semibold type on the
   `font` shorthand and a 44px minimum height; it sets the tap highlight and the touch action as the source does. Back in (a skipped stop) is a
   12px semibold chip in the accent ink, 8px by 12px of padding, a 10px radius and a 40px minimum height. The off-territory name is a bare
   left-aligned line that fills its row and ellipsizes. An unroutable row (a stop with no confirmed location) is a full width flex line with
   a dashed card border, 7px by 8px of padding and a 4px top margin, in 12.5px semibold type. Each reads the exact legacy roles, and the
   declaration order is the order of the source. The caller owns the labels, the glyphs and the `wedge-hoverable` class of the unroutable row. */
const TOGGLE_PADDING = '11px 14px', TOGGLE_RADIUS = 12, TOGGLE_HEIGHT = 44, TOGGLE_GAP = 8, TOGGLE_MARGIN = 14;
const BACK_PADDING = '8px 12px', BACK_RADIUS = 10, BACK_HEIGHT = 40;
const UNROUTABLE_PADDING = '7px 8px', UNROUTABLE_RADIUS = 8, UNROUTABLE_FONT = 12.5;

export function wedgeRoutePlanStyle(look: WedgeRoutePlanLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'route-plan-toggle') return {
    width: '100%', marginTop: TOGGLE_MARGIN, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: TOGGLE_GAP,
    padding: TOGGLE_PADDING, borderRadius: TOGGLE_RADIUS, border: `1px solid ${r('cardBorder')}`, background: r('bgElev'), color: r('label'),
    font: `600 14px ${r('font')}`, cursor: 'pointer', minHeight: TOGGLE_HEIGHT, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
  if (look === 'route-plan-back') return {
    flex: '0 0 auto', padding: BACK_PADDING, borderRadius: BACK_RADIUS, border: `1px solid ${r('cardBorder')}`, background: r('bgElev'),
    color: r('accent'), font: `600 12px ${r('font')}`, cursor: 'pointer', minHeight: BACK_HEIGHT,
  };
  if (look === 'route-plan-far') return {
    flex: 1, minWidth: 0, background: 'none', border: 0, padding: 0, textAlign: 'left', font: `500 14px/1.3 ${r('font')}`, color: r('labelSec'),
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
  };
  return {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: UNROUTABLE_PADDING, marginTop: 4,
    borderRadius: UNROUTABLE_RADIUS, border: `1px dashed ${r('cardBorder')}`, background: 'transparent', color: r('label'), cursor: 'pointer',
    fontFamily: r('font'), fontSize: UNROUTABLE_FONT, fontWeight: 600, lineHeight: 1.3,
  };
}

/* No look draws the transparent target. Back in kept it first, and a browser check against legacy found that its anchor (position and
   isolation) changed the row raster by about 14,000 to 18,000 pixels at 1440 wide in light and sun, and grew the tap area by a 2px ring. With
   the anchor off the raster matches. The toggle is 44px tall already, and the off-territory name and the unroutable row sit in stacked rows
   4px or less apart, where a region would overlap the next row. */
export const wedgeRoutePlanDrawsHit = (look: string): boolean => !isWedgeRoutePlanLook(look);
